
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, Loader2 } from "lucide-react";
import { useVideoUpload } from "@/hooks/useVideoUpload";
import VideoFileInput from "./video-upload/VideoFileInput";
import ErrorDisplay from "./video-upload/ErrorDisplay";
import ProgressBar from "./video-upload/ProgressBar";

interface VideoUploadDialogProps {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  onUploadComplete: (videoUrl: string, videoTitle: string) => void;
}

const VideoUploadDialog = ({ 
  showDialog, 
  setShowDialog, 
  onUploadComplete 
}: VideoUploadDialogProps) => {
  const {
    videoTitle,
    setVideoTitle,
    videoFile,
    isUploading,
    uploadProgress,
    error,
    handleFileChange,
    handleUpload,
    resetForm
  } = useVideoUpload({
    onUploadComplete,
    onClose: () => setShowDialog(false)
  });

  return (
    <Dialog 
      open={showDialog} 
      onOpenChange={(open) => {
        if (!open) resetForm();
        setShowDialog(open);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Video</DialogTitle>
          <DialogDescription>
            Upload a video for player and ball tracking analysis
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Video Title</Label>
            <Input
              id="title"
              placeholder="Enter a title for this video"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              disabled={isUploading}
            />
          </div>
          
          <VideoFileInput 
            videoFile={videoFile}
            isUploading={isUploading}
            onFileChange={handleFileChange}
          />
          
          <ErrorDisplay error={error} />
          <ProgressBar progress={uploadProgress} isUploading={isUploading} />
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowDialog(false)}
            disabled={isUploading}
            className="sm:mr-2"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!videoFile || isUploading || !videoTitle.trim()}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload Video
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;
